import React, { useState } from 'react';
import styled from 'styled-components';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@/se/components/Button[DEPRECATED]';
import { Modal, Title, Subtitle, ModalAction } from '../../../tablet/Modal';

interface DischargeBothLastPatientModalProps {
  physicianName?: string;
  physicianPhone?: string;
  anesthesiologistName?: string;
  anesthesiologistPhone?: string;
  onConfirm: (sendToPhysician: boolean, sendToAnesthesiologist: boolean) => void;
  onCancel: () => void;
  working?: boolean;
}

/**
 * Modal for discharging a patient who is the last patient for both
 * the physician AND the anesthesiologist. Allows user to select
 * which recipients should receive the daily recap message.
 */
export function DischargeBothLastPatientModal({
  physicianName,
  physicianPhone,
  anesthesiologistName,
  anesthesiologistPhone,
  onConfirm,
  onCancel,
  working = false,
}: DischargeBothLastPatientModalProps) {
  const [sendToPhysician, setSendToPhysician] = useState(true);
  const [sendToAnesthesiologist, setSendToAnesthesiologist] = useState(true);

  const handleConfirm = () => {
    onConfirm(sendToPhysician, sendToAnesthesiologist);
  };

  const hasAnySelected = sendToPhysician || sendToAnesthesiologist;

  return (
    <Modal
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      style={{ overflowY: 'auto' }}
    >
      <Title>Last Patient - Send Daily Recap</Title>
      <Subtitle>
        This patient is the last patient of the day for both the physician and anesthesiologist.
        <br />
        Select who should receive daily recap messages:
      </Subtitle>

      <CheckboxSection onClick={e => e.stopPropagation()}>
        <CheckboxRow>
          <StyledFormControlLabel
            control={
              <StyledCheckbox
                checked={sendToPhysician}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSendToPhysician(e.target.checked)}
              />
            }
            label={
              <CheckboxLabelContent>
                <RecipientTitle>Physician: {physicianName || 'Unknown'}</RecipientTitle>
                <RecipientDetail>{physicianPhone || 'No phone number'}</RecipientDetail>
              </CheckboxLabelContent>
            }
          />
          {sendToPhysician && (
            <MessagePreview>
              Hi {'{{physicianName}}'}, your last patient {'{{patientFullName}}'} was discharged. Here are your
              analytics for the day: Average PIPO, Average Surgery Time, Average Closing Time, Average Turnover.
            </MessagePreview>
          )}
        </CheckboxRow>

        <CheckboxRow>
          <StyledFormControlLabel
            control={
              <StyledCheckbox
                checked={sendToAnesthesiologist}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSendToAnesthesiologist(e.target.checked)}
              />
            }
            label={
              <CheckboxLabelContent>
                <RecipientTitle>Anesthesiologist: {anesthesiologistName || 'Unknown'}</RecipientTitle>
                <RecipientDetail>{anesthesiologistPhone || 'No phone number'}</RecipientDetail>
              </CheckboxLabelContent>
            }
          />
          {sendToAnesthesiologist && (
            <MessagePreview>
              Hi {'{{anesthesiologistName}}'}, your last patient {'{{patientFullName}}'} was discharged. Here are your
              analytics for the day: Average Patient In to Anesthesia Start, Average Anesthesia Start to Complete.
            </MessagePreview>
          )}
        </CheckboxRow>
      </CheckboxSection>

      <ModalAction>
        {hasAnySelected && (
          <Button xl primary onClick={handleConfirm} disabled={working}>
            Discharge and Send
          </Button>
        )}
        <Button xl onClick={() => onConfirm(false, false)} disabled={working}>
          Discharge
        </Button>
        <Button xl onClick={onCancel} disabled={working}>
          Cancel
        </Button>
      </ModalAction>
    </Modal>
  );
}

const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 2rem 0;
  width: 100%;
  max-width: 40rem;
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.textColor.alpha(0.05).string()};
  border-radius: 0.5rem;
  padding: 1rem;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin: 0 !important;

  .MuiFormControlLabel-label {
    flex: 1;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: ${props => props.theme.textColor.alpha(0.5).string()};
  }
  &.Mui-checked {
    color: ${props => props.theme.button.primary.backgroundColor.string()};
  }
`;

const CheckboxLabelContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const RecipientTitle = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
`;

const RecipientDetail = styled.span`
  font-size: 1rem;
  opacity: 0.6;
  margin-top: 0.25rem;
`;

const MessagePreview = styled.p`
  position: relative;
  padding: 1em 1.5em;
  border-radius: 0.25em;
  margin-top: 1rem;
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 1.5;
  background-color: ${props => props.theme.textColor.alpha(0.1).string()};
  text-align: left;
  opacity: 0.8;
  font-style: italic;
`;
